import {
  ATTENDANCE_SHIFTS,
  ATTENDANCE_WEEKDAYS,
  type AttendanceShift,
  type AttendanceShiftsByDay,
  type AttendanceWeekday,
  type ShiftMode,
} from '@seen/core/models/schedules/dayCenterSchedule'

// Full labels for the card / history (e.g. "Mon, Wed, Fri").
export const WEEKDAY_FULL_LABELS: Record<AttendanceWeekday, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
}

// Single-letter labels for the circular day toggles (disambiguated by tooltip).
export const WEEKDAY_LETTER_LABELS: Record<AttendanceWeekday, string> = {
  mon: 'M',
  tue: 'T',
  wed: 'W',
  thu: 'T',
  fri: 'F',
}

// Full names for the day-toggle tooltips.
export const WEEKDAY_LONG_LABELS: Record<AttendanceWeekday, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
}

export function formatAttendanceDays(days: AttendanceWeekday[]): string {
  return days.map((day) => WEEKDAY_FULL_LABELS[day]).join(', ')
}

// Like `formatAttendanceDays`, but collapses runs of 3+ consecutive weekdays
// into an en-dash range: Mon/Tue/Wed → "Mon–Wed", Mon–Fri → "Mon–Fri". Runs of
// 1–2 stay comma-separated (Mon/Wed → "Mon, Wed"). "Consecutive" means adjacent
// in weekday order. Used on the facesheet header row and hover card, where the
// extra compactness helps; other surfaces keep the plain comma list.
export function formatAttendanceDayRanges(days: AttendanceWeekday[]): string {
  const ordered = ATTENDANCE_WEEKDAYS.filter((day) => days.includes(day))
  const parts: string[] = []
  let runStart = 0
  for (let i = 1; i <= ordered.length; i++) {
    const consecutive =
      i < ordered.length &&
      ATTENDANCE_WEEKDAYS.indexOf(ordered[i]) ===
        ATTENDANCE_WEEKDAYS.indexOf(ordered[i - 1]) + 1
    if (consecutive) {
      continue
    }
    // ordered[runStart..i-1] is now a maximal consecutive run.
    if (i - runStart >= 3) {
      parts.push(
        `${WEEKDAY_FULL_LABELS[ordered[runStart]]}–${WEEKDAY_FULL_LABELS[ordered[i - 1]]}`
      )
    } else {
      for (let k = runStart; k < i; k++) {
        parts.push(WEEKDAY_FULL_LABELS[ordered[k]])
      }
    }
    runStart = i
  }
  return parts.join(', ')
}

export function daysPerWeekLabel(count: number): string {
  return `${count} ${count === 1 ? 'day' : 'days'}/week`
}

export const SHIFT_LABELS: Record<AttendanceShift, string> = {
  am: 'AM',
  pm: 'PM',
}

export const SHIFT_MODE_OPTIONS: readonly {
  value: ShiftMode
  label: string
}[] = [
  { value: 'am', label: 'AM' },
  { value: 'pm', label: 'PM' },
  { value: 'custom', label: 'Custom' },
]

export const SHIFT_OPTIONS: readonly {
  value: AttendanceShift
  label: string
}[] = [
  { value: 'am', label: 'AM' },
  { value: 'pm', label: 'PM' },
]

export function shiftModeLabel(mode: ShiftMode): string {
  return mode === 'custom' ? 'custom' : SHIFT_LABELS[mode]
}

// Takes the mode rather than deriving it, so the summary always agrees with
// the shift control beside it — picking Custom while every day happens to
// share a shift would otherwise still read "PM shift".
// e.g. "3 days/week, PM shift", or just "0 days/week" when no shift is set.
export function dayCenterDaysSummary(
  days: AttendanceWeekday[],
  mode: ShiftMode | undefined
): string {
  const base = daysPerWeekLabel(days.length)
  return mode ? `${base}, ${shiftModeLabel(mode)} shift` : base
}

// e.g. "2 days/week, AM preferred".
export function preferredDaysSummary(
  days: AttendanceWeekday[],
  mode: ShiftMode | undefined
): string {
  const base = daysPerWeekLabel(days.length)
  return mode ? `${base}, ${shiftModeLabel(mode)} preferred` : base
}

export interface ShiftDayGroup {
  days: AttendanceWeekday[]
  // The shift shared by every day in the group, or undefined when these days
  // have no shift recorded (rendered without an AM/PM pill).
  shift: AttendanceShift | undefined
}

// Groups attended days by the shift worked — AM first, then PM, then any days
// with no shift recorded — so the facesheet reads "Mon, Wed [AM] · Thu [PM]".
// Days keep weekday order within each group, so e.g. Mon/Wed/Fri on AM and Thu
// on PM groups as "Mon, Wed, Fri · Thu", not the raw weekday sequence.
export function groupDaysByShift(
  days: AttendanceWeekday[],
  shifts: AttendanceShiftsByDay
): ShiftDayGroup[] {
  const groups: ShiftDayGroup[] = []
  for (const shift of ATTENDANCE_SHIFTS) {
    const shiftDays = days.filter((day) => shifts[day] === shift)
    if (shiftDays.length > 0) {
      groups.push({ days: shiftDays, shift })
    }
  }
  const unshifted = days.filter((day) => !shifts[day])
  if (unshifted.length > 0) {
    groups.push({ days: unshifted, shift: undefined })
  }
  return groups
}
